import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { supabase } from '@/shared/config/supabase';
import {
  TerminologyMap,
  DEFAULT_TERMINOLOGY,
  mergeWithDefaults,
  getTerminologyOverrides,
} from '@/features/settings/domain/entities/TerminologyDefaults';

interface TerminologyState {
  terminology: TerminologyMap;
  workspaceId: number | null;
  lastFetched: number | null;

  // Actions
  setTerminology: (overrides: Partial<TerminologyMap>) => void;
  updateKey: (key: keyof TerminologyMap, value: string) => void;
  resetKey: (key: keyof TerminologyMap) => void;
  resetAll: () => void;

  // Supabase persistence (UI-only concern, no application/infrastructure layer)
  loadFromDB: (workspaceId: number) => Promise<void>;
  saveToDB: (workspaceId: number) => Promise<void>;
}

export const useTerminologyStore = create<TerminologyState>()(
  persist(
    (set, get) => ({
      terminology: { ...DEFAULT_TERMINOLOGY },
      workspaceId: null,
      lastFetched: null,

      setTerminology: (overrides) =>
        set({
          terminology: mergeWithDefaults(overrides),
          lastFetched: Date.now(),
        }),

      updateKey: (key, value) =>
        set((state) => ({
          terminology: { ...state.terminology, [key]: value },
          lastFetched: Date.now(),
        })),

      resetKey: (key) =>
        set((state) => ({
          terminology: { ...state.terminology, [key]: DEFAULT_TERMINOLOGY[key] },
          lastFetched: Date.now(),
        })),

      resetAll: () =>
        set({
          terminology: { ...DEFAULT_TERMINOLOGY },
          lastFetched: Date.now(),
        }),

      loadFromDB: async (workspaceId) => {
        try {
          const { data, error } = await supabase
            .from('Workspaces')
            .select('settings')
            .eq('id', workspaceId)
            .single();

          if (error) {
            console.error('[Terminology] Error loading from DB:', error);
            return;
          }

          const settings = (data?.settings as Record<string, unknown>) || {};
          const overrides = (settings.terminology as Partial<TerminologyMap>) || {};

          set({
            terminology: mergeWithDefaults(overrides),
            workspaceId,
            lastFetched: Date.now(),
          });
        } catch (error) {
          console.error('[Terminology] Unexpected error loading from DB:', error);
        }
      },

      saveToDB: async (workspaceId) => {
        try {
          // Read current settings to merge
          const { data: currentData, error: fetchError } = await supabase
            .from('Workspaces')
            .select('settings')
            .eq('id', workspaceId)
            .single();

          if (fetchError) {
            throw new Error(`Error al obtener configuración: ${fetchError.message}`);
          }

          const currentSettings = (currentData?.settings as Record<string, unknown>) || {};
          const overrides = getTerminologyOverrides(get().terminology);

          const updatedSettings = {
            ...currentSettings,
            terminology: Object.keys(overrides).length > 0 ? overrides : undefined,
          };

          // Remove the key entirely if no overrides
          if (!updatedSettings.terminology) {
            delete updatedSettings.terminology;
          }

          const { data: updatedRow, error: updateError } = await supabase
            .from('Workspaces')
            .update({ settings: updatedSettings })
            .eq('id', workspaceId)
            .select('id')
            .single();

          if (updateError) {
            if (updateError.code === '42501' || updateError.message?.includes('policy')) {
              throw new Error('No tienes permisos para actualizar la terminología.');
            }
            throw new Error(`Error al guardar terminología: ${updateError.message}`);
          }

          if (!updatedRow) {
            throw new Error('No se pudo actualizar la terminología. Verifica tus permisos.');
          }
        } catch (error) {
          console.error('[Terminology] Error saving to DB:', error);
          throw error;
        }
      },
    }),
    {
      name: 'terminology-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        terminology: state.terminology,
        workspaceId: state.workspaceId,
        lastFetched: state.lastFetched,
      }),
    }
  )
);

/**
 * Selectors for optimized rendering
 */
export const terminologySelectors = {
  selectTerminology: (state: TerminologyState) => state.terminology,
  selectWorkspaceId: (state: TerminologyState) => state.workspaceId,
};
